import React from "react";
import { motion } from "framer-motion";
import { Eye, Phone, TrendingDown, TrendingUp } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { th } from "date-fns/locale";
import { cn } from "../../lib/utils";
import { Badge, Button, Avatar } from "../ui";

type PatientStatus = "ok" | "alert" | "missed";

interface Patient {
  id:        string;
  name:      string;
  age:       number;
  hn:        string;
  diseases:  string[];
  adherence: number;
  status:    PatientStatus;
  lastSeen:  Date;
}

interface PatientTableProps {
  patients: Patient[];
}

const statusConfig: Record<PatientStatus, { badge: "green" | "red" | "orange"; label: string }> = {
  ok:     { badge: "green",  label: "ปกติ"        },
  alert:  { badge: "red",    label: "มีการแจ้งเตือน" },
  missed: { badge: "orange", label: "ขาดยา"       },
};

function AdherenceBar({ value }: { value: number }) {
  const color = value >= 80 ? "bg-green-500" : value >= 60 ? "bg-orange-400" : "bg-red-500";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <motion.div
          className={cn("h-full rounded-full", color)}
          initial={{ width: 0 }}
          animate={{ width: `${value}%` }}
          transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
        />
      </div>
      <span className={cn(
        "text-xs font-semibold w-9 text-right",
        value >= 80 ? "text-green-600" : value >= 60 ? "text-orange-500" : "text-red-500"
      )}>
        {value}%
      </span>
    </div>
  );
}

export function PatientTable({ patients }: PatientTableProps) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100">
            {["ผู้ป่วย", "HN / โรค", "อัตราการรับยา", "สถานะ", "พบล่าสุด", ""].map(h => (
              <th key={h} className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {patients.map((p, i) => {
            const cfg = statusConfig[p.status];
            return (
              <motion.tr
                key={p.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="hover:bg-gray-50/70 transition-colors group"
              >
                {/* Patient */}
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <Avatar name={p.name} size="sm" status={p.status === "ok" ? "online" : p.status === "missed" ? "busy" : "offline"} />
                    <div>
                      <p className="font-medium text-gray-900">{p.name}</p>
                      <p className="text-xs text-gray-400">อายุ {p.age} ปี</p>
                    </div>
                  </div>
                </td>

                {/* HN / Diseases */}
                <td className="px-6 py-4">
                  <p className="text-xs font-mono text-gray-500">{p.hn}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {p.diseases.map(d => (
                      <span key={d} className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-md font-medium">{d}</span>
                    ))}
                  </div>
                </td>

                {/* Adherence */}
                <td className="px-6 py-4 min-w-[140px]">
                  <AdherenceBar value={p.adherence} />
                </td>

                {/* Status */}
                <td className="px-6 py-4">
                  <Badge variant={cfg.badge} dot>{cfg.label}</Badge>
                </td>

                {/* Last Seen */}
                <td className="px-6 py-4 text-xs text-gray-400 whitespace-nowrap">
                  {formatDistanceToNow(p.lastSeen, { addSuffix: true, locale: th })}
                </td>

                {/* Actions */}
                <td className="px-6 py-4">
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="sm" icon={<Eye className="w-3.5 h-3.5" />}>ดู</Button>
                    <Button variant="ghost" size="sm" icon={<Phone className="w-3.5 h-3.5" />}>โทร</Button>
                  </div>
                </td>
              </motion.tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
